/**
 * Shared facts about the files the ingestion pipeline accepts.
 *
 * These were declared inside SubmissionFileUploadDrawer while it was the only place
 * that uploaded ingestion files. AttachFilesDialog now uploads them too, and both must
 * agree on what the backend will accept — ProcessingConfig.get_processors_for_file in
 * services/ai_agents/ingestion_file_management/config.py is the other side of this
 * contract, and a file the UI accepts but the backend cannot read is reported as a
 * folder that "could not read any file".
 */
import { saveFile, triggerBlobDownload } from "fileUtils";

// Must stay a superset-free mirror of ProcessingConfig.PROCESSORS' extensions, plus zip
// (expanded server-side by _expand_zip_file rather than processed directly).
export const SUPPORTED_EXTENSIONS = new Set([
  "pdf",
  "docx",
  "xls",
  "xlsx",
  "xlsm",
  "csv",
  "tsv",
  "txt",
  "html",
  "zip",
]);

// Email intake writes the message body as a file alongside the attachments; it is shown
// as an email rather than listed as a document. Mirrors
// ProcessingConfig.EMAIL_BODY_FILENAMES.
export const EMAIL_BODY_FILENAMES = new Set([
  "email_body.html",
  "email_body.txt",
]);

// Presigned URLs are registered with the backend and re-read by background processing
// days later, so they are minted with the longest expiry S3 allows (7 days). The server
// re-signs from the S3 key when it needs a file after that.
export const FILE_URL_EXPIRY_SECONDS = 604800;

export const isSupportedIngestionFile = (fileName) =>
  SUPPORTED_EXTENSIONS.has(String(fileName).split(".").pop().toLowerCase());

// Names an operating system or Office writes beside real documents. The ones that matter
// carry a document's extension: macOS "._name.pdf" sidecars and Word/Excel "~$name.docx"
// owner files both pass an extension check. Mirrors
// ProcessingConfig.SYSTEM_ARTIFACT_* in
// services/ai_agents/ingestion_file_management/config.py.
const SYSTEM_ARTIFACT_DIRS = new Set([
  "__MACOSX",
  "$RECYCLE.BIN",
  "System Volume Information",
]);
const SYSTEM_ARTIFACT_NAMES = new Set([
  ".ds_store",
  ".localized",
  "thumbs.db",
  "ehthumbs.db",
  "desktop.ini",
]);
const SYSTEM_ARTIFACT_PREFIXES = ["._", "~$"];

/** Whether this is machine bookkeeping rather than a document someone sent. */
export const isSystemArtifact = (fileName) => {
  const segments = String(fileName || "")
    .replace(/\\/g, "/")
    .split("/")
    .filter(Boolean);
  if (segments.some((seg) => SYSTEM_ARTIFACT_DIRS.has(seg))) return true;
  const base = segments[segments.length - 1] || "";
  return (
    SYSTEM_ARTIFACT_NAMES.has(base.toLowerCase()) ||
    SYSTEM_ARTIFACT_PREFIXES.some((prefix) => base.startsWith(prefix))
  );
};

/**
 * Whether this file is one triage can actually read.
 *
 * A .zip uploads and registers but is expanded server-side and never read, so like a
 * system artifact it cannot represent a folder — picking one as the representative sends
 * it to triage on its own. Mirrors ProcessingConfig.get_processors_for_file.
 */
export const isReadableIngestionFile = (fileName) => {
  if (isSystemArtifact(fileName)) return false;
  const ext = String(fileName || "")
    .split(".")
    .pop()
    .toLowerCase();
  return SUPPORTED_EXTENSIONS.has(ext) && ext !== "zip";
};

export const formatFileSize = (bytes) => {
  const mb = bytes / 1024 / 1024;
  if (mb >= 0.01) return `${mb.toFixed(2)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

/**
 * Download one triage source document.
 *
 * App-uploaded files are served by the files service. A ZIP member is not: its id is minted
 * by ai_agents, which the files service has never heard of, so it is asked for the URL
 * instead — the only service that can sign for the prefix those members live under.
 */
export const downloadIngestionSource = async (
  fileId,
  filename,
  getAgentUrl
) => {
  try {
    await saveFile(fileId, filename);
    return;
  } catch (filesServiceError) {
    const res = await getAgentUrl({ fileId }).unwrap();
    if (!res?.url) throw filesServiceError;
    await triggerBlobDownload(res.url, filename);
  }
};
